import { Frame, RosterEntry, WEAPON_NAMES, KillEvent, BulletImpactEvent, EntityStartEvent, BombPlantEvent, SmokeThrowEvent, ReplayEvent, PlayerFrame, WorldState, SmokeBreak } from './replayParser';
import { PreloadedIcons } from './weaponIcons';
import { mapImageMappings, mapConfigs, multiLevelMaps, type MultiLevelMapConfig } from '../../../content/maps/mapData';

export { mapImageMappings, mapConfigs, multiLevelMaps };

const T_COLOR = '#de9b35';
const CT_COLOR = '#5d79ae';
const T_COLOR_LIGHT = '#f0c060';
const CT_COLOR_LIGHT = '#80a0d0';
const DEAD_COLOR = '#555555';

const SMOKE_DURATION_TICKS = 64 * 18; // 18 seconds
const FIRE_DURATION_SECONDS = 7;
const SMOKE_RADIUS_WORLD = 150;
// Smoke-break visual: HE detonating in/near a smoke punches a temporary hole
// at the impact point. Smoke isn't removed — only a chunk is missing — so the
// surrounding cloud still renders normally and the map shows through the gap.
const SMOKE_BREAK_HOLE_FRAMES = 96;        // ~1.5s @ 64Hz, full hole lifecycle
const SMOKE_BREAK_HOLE_RAMP = 0.12;        // fraction of lifecycle spent ramping up
const SMOKE_BREAK_HOLE_HOLD = 0.45;        // fraction spent at full size
const SMOKE_BREAK_HOLE_RADIUS_WORLD = 110; // peak hole radius in world units (smoke is 150)
const SMOKE_BREAK_SHOCKWAVE_FRAMES = 30;   // ~0.47s, expanding outline ring lifetime
const FIRE_RADIUS_WORLD = 105;       // v1 fallback: single circle
const FLAME_RADIUS_WORLD = 48;       // v2: individual flame point radius (large enough to overlap and fill gaps)
const PLAYER_RADIUS = 9;
const VIEW_CONE_LENGTH = 22;
const VIEW_CONE_ANGLE = 15; // degrees half-width

export interface KillFeedEntry {
    killerSlot: number;
    victimSlot: number;
    weaponId: number;
    headshot: boolean;
    wallbang: boolean;
    noscope: boolean;
    thrusmoke: boolean;
    frameIdx: number;
}

export interface FlashPopEntry {
    x: number;
    y: number;
    frameIdx: number;
    grenadeType: 'flash' | 'he';
}

export interface DeathMarker {
    x: number;
    y: number;
    team: 'T' | 'CT';
    frameIdx: number;
}

export interface TracerEntry {
    shooterX: number;
    shooterY: number;
    impactX: number;
    impactY: number;
    frameIdx: number;
    shooterSlot: number;
}

export interface HitMarkerEntry {
    attackerX: number;
    attackerY: number;
    victimX: number;
    victimY: number;
    attackerSlot: number;
    frameIdx: number;
}

export interface GrenadeTrajectory {
    throwerSlot: number;
    startX: number;
    startY: number;
    endX: number;
    endY: number;
    frameIdx: number;
    type: string;
    entityId: number;
}

export function worldToCanvas(
    worldX: number, worldY: number,
    config: { pos_x: number; pos_y: number; scale: number },
    canvasSize: number,
): { x: number; y: number } {
    const radarX = (worldX - config.pos_x) / config.scale;
    const radarY = (config.pos_y - worldY) / config.scale;
    const scale = canvasSize / 1024;
    return { x: radarX * scale, y: radarY * scale };
}

function worldRadius(radiusWorld: number, config: { scale: number }, canvasSize: number): number {
    return (radiusWorld / config.scale) * (canvasSize / 1024);
}

function getPlayerName(roster: RosterEntry[], slot: number, playerNames: Record<string, string>): string {
    const entry = roster[slot];
    if (!entry) return `P${slot + 1}`;
    return playerNames[entry.steamId] || `P${slot + 1}`;
}

export function renderFrame(
    ctx: CanvasRenderingContext2D,
    frame: Frame,
    roster: RosterEntry[],
    mapName: string,
    mapImage: HTMLImageElement,
    canvasSize: number,
    killFeed: KillFeedEntry[],
    currentFrameIdx: number,
    playerNames: Record<string, string> = {},
    tracers: TracerEntry[] = [],
    grenadeTrajectories: GrenadeTrajectory[] = [],
    smokeTeams: Record<number, 'T' | 'CT'> = {},
    preloadedIcons: PreloadedIcons | null = null,
    flashPops: FlashPopEntry[] = [],
    fireTeams: Record<number, 'T' | 'CT'> = {},
    fireStartFrames: Record<number, number> = {},
    projPaths: Map<string, Array<Array<{ x: number; y: number }>>> = new Map(),
    flashBlinds: Array<{ victimSlot: number; frameIdx: number; durationSeconds: number }> = [],
    deathMarkers: DeathMarker[] = [],
    hitMarkers: HitMarkerEntry[] = [],
    isBombPlanted: boolean = false,
    bombPlantFrame: number | null = null,
    activeDefuse: { startFrame: number; hasKit: boolean } | null = null,
    bombDefused: boolean = false,
    smokeClipEntityIds: Set<number> = new Set(),
    smokeBreaks: SmokeBreak[] = [],
    bombExploded: { frameIdx: number; x: number; y: number } | null = null,
    /** Drawn immediately after the radar image, so it sits under every entity. Used by the
     *  map-control overlay, which must tint the ground without washing out players or utility. */
    underlay: ((ctx: CanvasRenderingContext2D, canvasSize: number) => void) | null = null,
) {
    const config = mapConfigs[mapName];
    if (!config) return;

    // Index the most recent active break per smoke entity, so the per-smoke loop is O(1)
    const breakWindow = Math.max(SMOKE_BREAK_HOLE_FRAMES, SMOKE_BREAK_SHOCKWAVE_FRAMES);
    const activeBreakBySmoke = new Map<number, SmokeBreak>();
    for (const brk of smokeBreaks) {
        if (currentFrameIdx < brk.frameIdx) continue;
        if (currentFrameIdx - brk.frameIdx > breakWindow) continue;
        const existing = activeBreakBySmoke.get(brk.smokeEntityId);
        if (!existing || brk.frameIdx > existing.frameIdx) {
            activeBreakBySmoke.set(brk.smokeEntityId, brk);
        }
    }

    // Fill background in zoomed space so map image edges blend with canvas background
    ctx.fillStyle = '#1a1a1a';
    ctx.fillRect(0, 0, canvasSize, canvasSize);
    ctx.drawImage(mapImage, 0, 0, canvasSize, canvasSize);

    if (underlay) underlay(ctx, canvasSize);

    // Death markers — team-tinted skull icons where players died
    const skullIcon = preloadedIcons?.indicators?.skull;
    // Pre-create tinted skulls using offscreen canvas to avoid compositing issues
    const tintedSkulls: Record<string, HTMLCanvasElement> = {};
    if (skullIcon) {
        for (const team of ['T', 'CT'] as const) {
            const sz = 12;
            const offscreen = document.createElement('canvas');
            offscreen.width = sz;
            offscreen.height = sz;
            const offCtx = offscreen.getContext('2d')!;
            offCtx.drawImage(skullIcon, 0, 0, sz, sz);
            offCtx.globalCompositeOperation = 'source-atop';
            offCtx.fillStyle = team === 'T' ? T_COLOR : CT_COLOR;
            offCtx.fillRect(0, 0, sz, sz);
            tintedSkulls[team] = offscreen;
        }
    }
    for (const dm of deathMarkers) {
        if (dm.frameIdx > currentFrameIdx) continue;
        const dPos = worldToCanvas(dm.x, dm.y, config, canvasSize);
        ctx.globalAlpha = 0.55;
        const tinted = tintedSkulls[dm.team];
        if (tinted) {
            const sz = 12;
            ctx.drawImage(tinted, dPos.x - sz / 2, dPos.y - sz / 2, sz, sz);
        } else {
            // Fallback X marker
            ctx.strokeStyle = dm.team === 'T' ? T_COLOR : CT_COLOR;
            ctx.lineWidth = 2;
            ctx.beginPath();
            ctx.moveTo(dPos.x - 4, dPos.y - 4);
            ctx.lineTo(dPos.x + 4, dPos.y + 4);
            ctx.moveTo(dPos.x + 4, dPos.y - 4);
            ctx.lineTo(dPos.x - 4, dPos.y + 4);
            ctx.stroke();
        }
        ctx.globalAlpha = 1;
    }

    // Smoke clouds with timer ring
    for (const smoke of frame.world.smokes) {
        const pos = worldToCanvas(smoke.x, smoke.y, config, canvasSize);
        const r = worldRadius(SMOKE_RADIUS_WORLD, config, canvasSize);

        // Smoke-break hole: ramp up, hold, then shrink as the smoke fills back in.
        // Hole position is the HE impact, clamped inward so it never extends past
        // the smoke edge (else even-odd fill would draw a phantom smoke patch).
        let holeRadius = 0;
        let holeCx = 0, holeCy = 0;
        const activeBreak = activeBreakBySmoke.get(smoke.entityId);
        if (activeBreak) {
            const breakAge = currentFrameIdx - activeBreak.frameIdx;
            if (breakAge >= 0 && breakAge < SMOKE_BREAK_HOLE_FRAMES) {
                const t = breakAge / SMOKE_BREAK_HOLE_FRAMES;
                let factor: number;
                if (t < SMOKE_BREAK_HOLE_RAMP) {
                    factor = t / SMOKE_BREAK_HOLE_RAMP; // expanding
                } else if (t < SMOKE_BREAK_HOLE_RAMP + SMOKE_BREAK_HOLE_HOLD) {
                    factor = 1; // hold at peak
                } else {
                    const tail = 1 - SMOKE_BREAK_HOLE_RAMP - SMOKE_BREAK_HOLE_HOLD;
                    factor = (1 - t) / tail; // closing
                }
                holeRadius = worldRadius(SMOKE_BREAK_HOLE_RADIUS_WORLD, config, canvasSize) * factor;

                // Clamp hole center inward so the cutout sits inside the smoke disk
                const dxW = activeBreak.x - smoke.x;
                const dyW = activeBreak.y - smoke.y;
                const distW = Math.hypot(dxW, dyW);
                const maxDistW = Math.max(0, SMOKE_RADIUS_WORLD - SMOKE_BREAK_HOLE_RADIUS_WORLD * factor);
                let holeWorldX = activeBreak.x;
                let holeWorldY = activeBreak.y;
                if (distW > maxDistW && distW > 0) {
                    const scale = maxDistW / distW;
                    holeWorldX = smoke.x + dxW * scale;
                    holeWorldY = smoke.y + dyW * scale;
                }
                const holeCanvas = worldToCanvas(holeWorldX, holeWorldY, config, canvasSize);
                holeCx = holeCanvas.x;
                holeCy = holeCanvas.y;
            }
        }

        // Smoke fill (team-colored). When a break is active, subtract a circular
        // hole at the impact via even-odd fill so the map shows through cleanly.
        const smokeTeam = smokeTeams[smoke.entityId];
        const smokeRgb = smokeTeam === 'T' ? '200,180,130' : smokeTeam === 'CT' ? '130,160,200' : '200,200,200';
        const gradient = ctx.createRadialGradient(pos.x, pos.y, 0, pos.x, pos.y, r);
        gradient.addColorStop(0, `rgba(${smokeRgb}, 0.55)`);
        gradient.addColorStop(0.7, `rgba(${smokeRgb}, 0.4)`);
        gradient.addColorStop(1, `rgba(${smokeRgb}, 0.1)`);
        ctx.beginPath();
        ctx.arc(pos.x, pos.y, r, 0, Math.PI * 2);
        if (holeRadius > 1) {
            // Counter-clockwise inner arc → even-odd subtracts this region from the fill
            ctx.arc(holeCx, holeCy, holeRadius, 0, Math.PI * 2, true);
        }
        ctx.fillStyle = gradient;
        ctx.fill('evenodd');

        // Thin warm-orange ring around the hole edge for extra emphasis
        if (holeRadius > 1) {
            ctx.beginPath();
            ctx.arc(holeCx, holeCy, holeRadius, 0, Math.PI * 2);
            ctx.strokeStyle = 'rgba(255, 170, 60, 0.7)';
            ctx.lineWidth = 1.5;
            ctx.stroke();
        }

        // Timer ring - fade out as smoke expires
        // Clamp to small minimum so icon/ring stay visible while smoke is still in frame data
        const elapsed = frame.tick - smoke.startTick;
        const remaining = Math.max(0.05, 1 - elapsed / SMOKE_DURATION_TICKS);

        ctx.beginPath();
        ctx.arc(pos.x, pos.y, r, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * remaining);
        ctx.strokeStyle = `rgba(255, 255, 255, ${0.4 * remaining})`;
        ctx.lineWidth = 2.5;
        ctx.stroke();

        // Smoke border
        ctx.beginPath();
        ctx.arc(pos.x, pos.y, r, 0, Math.PI * 2);
        ctx.strokeStyle = 'rgba(200, 200, 200, 0.3)';
        ctx.lineWidth = 1;
        ctx.stroke();

        // Smoke grenade icon in center
        const smokeIcon = preloadedIcons?.weapons[72]; // smokegrenade
        if (smokeIcon) {
            const iconSize = Math.max(r * 0.7, 14);
            const nw = smokeIcon.naturalWidth || smokeIcon.width || 10;
            const nh = smokeIcon.naturalHeight || smokeIcon.height || 10;
            const iconW = iconSize * (nw / nh);
            ctx.globalAlpha = 0.4 + remaining * 0.4;
            ctx.drawImage(smokeIcon, pos.x - iconW / 2, pos.y - iconSize / 2, iconW, iconSize);
            ctx.globalAlpha = 1;
        }

        // Clip available badge — small play button at bottom-right edge of smoke
        if (smokeClipEntityIds.has(smoke.entityId)) {
            const badgeR = Math.max(7, r * 0.16);
            const bx = pos.x + r * 0.62;
            const by = pos.y + r * 0.62;

            // Dark circle background
            ctx.beginPath();
            ctx.arc(bx, by, badgeR, 0, Math.PI * 2);
            ctx.fillStyle = 'rgba(0, 0, 0, 0.75)';
            ctx.fill();
            ctx.strokeStyle = 'rgba(255, 255, 255, 0.7)';
            ctx.lineWidth = 1;
            ctx.stroke();

            // Play triangle (shifted slightly right for optical center)
            const triH = badgeR * 0.65;
            const triW = triH * 0.9;
            const shift = triW * 0.1;
            ctx.beginPath();
            ctx.moveTo(bx - triW / 2 + shift, by - triH);
            ctx.lineTo(bx - triW / 2 + shift, by + triH);
            ctx.lineTo(bx + triW / 2 + shift, by);
            ctx.closePath();
            ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
            ctx.fill();
        }

        // Smoke-break shockwave — outline ring at HE impact point, expanding briefly.
        // Distinct from the generic HE detonation burst (which is a filled gradient).
        if (activeBreak) {
            const shockAge = currentFrameIdx - activeBreak.frameIdx;
            if (shockAge >= 0 && shockAge < SMOKE_BREAK_SHOCKWAVE_FRAMES) {
                const t = shockAge / SMOKE_BREAK_SHOCKWAVE_FRAMES;
                const shockPos = worldToCanvas(activeBreak.x, activeBreak.y, config, canvasSize);
                const shockRadius = 6 + t * 22;
                const shockAlpha = 0.9 * (1 - t);
                ctx.beginPath();
                ctx.arc(shockPos.x, shockPos.y, shockRadius, 0, Math.PI * 2);
                ctx.strokeStyle = `rgba(255, 170, 60, ${shockAlpha})`;
                ctx.lineWidth = 2;
                ctx.stroke();
            }
        }
    }

    // Fire zones — red/orange fire core + team-colored countdown ring
    for (const fire of frame.world.fires) {
        const center = worldToCanvas(fire.x, fire.y, config, canvasSize);
        const fireTeam = fireTeams[fire.uniqueId];
        const teamRgb = fireTeam === 'T' ? '222,155,53' : fireTeam === 'CT' ? '93,121,174' : '255,100,20';

        // Countdown timing
        const startFrame = fireStartFrames[fire.uniqueId];
        const elapsedFrames = startFrame != null ? currentFrameIdx - startFrame : 0;
        const totalFrames = FIRE_DURATION_SECONDS * 64;
        const remaining = startFrame != null ? Math.max(0, 1 - elapsedFrames / totalFrames) : 1;

        if (fire.flames.length > 0) {
            // V2: render individual flame circles, team-tinted
            const flameR = worldRadius(FLAME_RADIUS_WORLD, config, canvasSize);
            const alpha = remaining > 0 ? 0.7 + remaining * 0.3 : 0.7;

            // T = warm orange/gold, CT = blue, neutral = default orange
            const flameColor = fireTeam === 'T'
                ? `rgba(240,150,30,${alpha * 0.65})`
                : fireTeam === 'CT'
                ? `rgba(90,155,240,${alpha * 0.65})`
                : `rgba(240,120,20,${alpha * 0.65})`;

            ctx.fillStyle = flameColor;
            for (const flame of fire.flames) {
                const fp = worldToCanvas(flame.x, flame.y, config, canvasSize);
                ctx.beginPath();
                ctx.arc(fp.x, fp.y, flameR, 0, Math.PI * 2);
                ctx.fill();
            }
        } else {
            // V1 fallback: single fixed-radius circle
            const r = worldRadius(FIRE_RADIUS_WORLD, config, canvasSize);

            const fireGrad = ctx.createRadialGradient(center.x, center.y, 0, center.x, center.y, r);
            fireGrad.addColorStop(0, 'rgba(255, 80, 20, 0.5)');
            fireGrad.addColorStop(0.4, 'rgba(220, 50, 0, 0.35)');
            fireGrad.addColorStop(0.75, 'rgba(160, 30, 0, 0.15)');
            fireGrad.addColorStop(1, 'rgba(100, 20, 0, 0.02)');
            ctx.beginPath();
            ctx.arc(center.x, center.y, r, 0, Math.PI * 2);
            ctx.fillStyle = fireGrad;
            ctx.fill();

            if (remaining > 0) {
                ctx.beginPath();
                ctx.arc(center.x, center.y, r, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * remaining);
                ctx.strokeStyle = `rgba(${teamRgb}, ${0.6 + remaining * 0.4})`;
                ctx.lineWidth = 2.5;
                ctx.stroke();

                ctx.beginPath();
                ctx.arc(center.x, center.y, r, 0, Math.PI * 2);
                ctx.strokeStyle = `rgba(${teamRgb}, 0.3)`;
                ctx.lineWidth = 1;
                ctx.stroke();

                const fireIcon = preloadedIcons?.weapons[73];
                if (fireIcon) {
                    const iconSize = Math.max(r * 0.7, 14);
                    const fnw = fireIcon.naturalWidth || fireIcon.width || 10;
                    const fnh = fireIcon.naturalHeight || fireIcon.height || 10;
                    const iconW = iconSize * (fnw / fnh);
                    ctx.globalAlpha = 0.4 + remaining * 0.4;
                    ctx.drawImage(fireIcon, center.x - iconW / 2, center.y - iconSize / 2, iconW, iconSize);
                    ctx.globalAlpha = 1;
                }
            }
        }
    }

    // Dropped defuse kits
    for (const kit of frame.world.droppedKits) {
        const kitPos = worldToCanvas(kit.x, kit.y, config, canvasSize);

        // Subtle CT-blue glow
        ctx.beginPath();
        ctx.arc(kitPos.x, kitPos.y, 10, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(93, 121, 174, 0.25)';
        ctx.fill();

        const kitIcon = preloadedIcons?.equipment?.defuseKit;
        if (kitIcon) {
            const kh = 14;
            const knw = kitIcon.naturalWidth || kitIcon.width || 10;
            const knh = kitIcon.naturalHeight || kitIcon.height || 10;
            const kw = kh * (knw / knh);
            ctx.drawImage(kitIcon, kitPos.x - kw / 2, kitPos.y - kh / 2, kw, kh);
        } else {
            // Fallback: blue square with "K"
            ctx.fillStyle = 'rgba(93, 121, 174, 0.8)';
            ctx.fillRect(kitPos.x - 5, kitPos.y - 5, 10, 10);
            ctx.font = 'bold 7px Arial';
            ctx.fillStyle = '#ffffff';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText('K', kitPos.x, kitPos.y);
        }
    }

    // Dropped weapons & grenades (PLSR v3+; older replays have an empty list)
    for (const item of frame.world.droppedItems) {
        const p = worldToCanvas(item.x, item.y, config, canvasSize);
        const icon = preloadedIcons?.weapons[item.itemId];
        if (icon) {
            const isGrenade = item.itemId >= 70 && item.itemId <= 75;
            const h = isGrenade ? 11 : 12;
            const nw = icon.naturalWidth || icon.width || 10;
            const nh = icon.naturalHeight || icon.height || 10;
            const w = h * (nw / nh);
            // Faded so it's clearly distinct from a player carrying it
            ctx.globalAlpha = 0.55;
            ctx.drawImage(icon, p.x - w / 2, p.y - h / 2, w, h);
            ctx.globalAlpha = 1;
        } else {
            ctx.beginPath();
            ctx.arc(p.x, p.y, 3, 0, Math.PI * 2);
            ctx.fillStyle = 'rgba(200, 200, 200, 0.5)';
            ctx.fill();
        }
    }

    // Grenade detonation bursts (flash = bright white, HE = orange)
    const recentDetonations = flashPops.filter(f => currentFrameIdx - f.frameIdx < 60 && currentFrameIdx >= f.frameIdx);
    for (const det of recentDetonations) {
        const pos = worldToCanvas(det.x, det.y, config, canvasSize);
        const age = (currentFrameIdx - det.frameIdx) / 60;
        const isFlash = det.grenadeType === 'flash';

        if (isFlash) {
            // Flash — big bright white burst
            const radius = 15 + age * 50;
            const alpha = Math.max(0, 1 - age);

            // Solid white center that fades
            ctx.beginPath();
            ctx.arc(pos.x, pos.y, Math.max(0, 12 - age * 12), 0, Math.PI * 2);
            ctx.fillStyle = `rgba(255, 255, 255, ${alpha})`;
            ctx.fill();

            // Expanding glow
            const gradient = ctx.createRadialGradient(pos.x, pos.y, 0, pos.x, pos.y, radius);
            gradient.addColorStop(0, `rgba(255, 255, 255, ${alpha * 0.8})`);
            gradient.addColorStop(0.3, `rgba(230, 240, 255, ${alpha * 0.5})`);
            gradient.addColorStop(0.7, `rgba(200, 220, 255, ${alpha * 0.2})`);
            gradient.addColorStop(1, `rgba(180, 200, 255, 0)`);
            ctx.beginPath();
            ctx.arc(pos.x, pos.y, radius, 0, Math.PI * 2);
            ctx.fillStyle = gradient;
            ctx.fill();

            // Expanding ring
            ctx.beginPath();
            ctx.arc(pos.x, pos.y, radius * 0.7, 0, Math.PI * 2);
            ctx.strokeStyle = `rgba(255, 255, 255, ${alpha * 0.6})`;
            ctx.lineWidth = 2;
            ctx.stroke();
        } else {
            // HE — orange explosion burst
            const radius = 12 + age * 35;
            const alpha = Math.max(0, 0.9 - age * 0.9);

            // Bright center
            ctx.beginPath();
            ctx.arc(pos.x, pos.y, Math.max(0, 8 - age * 8), 0, Math.PI * 2);
            ctx.fillStyle = `rgba(255, 220, 80, ${alpha})`;
            ctx.fill();

            // Expanding fireball
            const gradient = ctx.createRadialGradient(pos.x, pos.y, 0, pos.x, pos.y, radius);
            gradient.addColorStop(0, `rgba(255, 160, 40, ${alpha * 0.7})`);
            gradient.addColorStop(0.4, `rgba(255, 80, 0, ${alpha * 0.4})`);
            gradient.addColorStop(1, `rgba(180, 30, 0, 0)`);
            ctx.beginPath();
            ctx.arc(pos.x, pos.y, radius, 0, Math.PI * 2);
            ctx.fillStyle = gradient;
            ctx.fill();
        }
    }

    // Projectiles in flight — team-colored icon + dashed line from throw origin.
    // Binary format gives no projectile entity ID, so paths are keyed by slot+type.
    // To handle the case where one player has multiple same-type grenades airborne
    // simultaneously, each key holds a list of paths and projectiles are matched to
    // their nearest existing path each frame.
    const PROJ_TO_WEAPON: Record<number, number> = { 1: 72, 2: 71, 3: 70, 4: 73, 6: 75 };
    const PROJ_PATH_MAX_JUMP = 600; // world units; beyond this, treat as a new path

    type ProjEntry = (typeof frame.world.projectiles)[number];
    type ProjPath = Array<{ x: number; y: number }>;

    // Group surviving projectiles by slot+type
    const projsByKey = new Map<string, ProjEntry[]>();
    for (const proj of frame.world.projectiles) {
        if (proj.type === 1 && frame.world.smokes.some(s =>
            Math.abs(s.x - proj.x) < 50 && Math.abs(s.y - proj.y) < 50)) continue;
        if (proj.type === 4 && frame.world.fires.some(f =>
            Math.abs(f.x - proj.x) < 50 && Math.abs(f.y - proj.y) < 50)) continue;
        if ((proj.type === 2 || proj.type === 3) && flashPops.some(d =>
            d.frameIdx <= currentFrameIdx &&
            Math.abs(d.x - proj.x) < 80 && Math.abs(d.y - proj.y) < 80)) continue;

        const slot = proj.throwerSlot !== 0xFF ? proj.throwerSlot : -1;
        const key = `${slot}_${proj.type}`;
        const list = projsByKey.get(key);
        if (list) list.push(proj); else projsByKey.set(key, [proj]);
    }

    // Match each projectile to its nearest existing path; prune unclaimed paths.
    const renderQueue: Array<{ proj: ProjEntry; path: ProjPath }> = [];
    for (const [key, projs] of Array.from(projsByKey.entries())) {
        const paths = projPaths.get(key) ?? [];
        const claimed = new Set<number>();
        const assigned: Array<ProjPath | null> = projs.map(() => null);

        // Greedy nearest-neighbor pairing
        while (true) {
            let bestProj = -1, bestPath = -1, bestDist = PROJ_PATH_MAX_JUMP;
            for (let pi = 0; pi < projs.length; pi++) {
                if (assigned[pi]) continue;
                const proj = projs[pi];
                for (let li = 0; li < paths.length; li++) {
                    if (claimed.has(li)) continue;
                    const tail = paths[li][paths[li].length - 1];
                    if (!tail) continue;
                    const d = Math.hypot(tail.x - proj.x, tail.y - proj.y);
                    if (d < bestDist) { bestDist = d; bestProj = pi; bestPath = li; }
                }
            }
            if (bestProj < 0) break;
            assigned[bestProj] = paths[bestPath];
            claimed.add(bestPath);
        }

        // Keep only claimed paths; unmatched projectiles get fresh paths
        const survivingPaths: ProjPath[] = [];
        for (let li = 0; li < paths.length; li++) {
            if (claimed.has(li)) survivingPaths.push(paths[li]);
        }
        for (let pi = 0; pi < projs.length; pi++) {
            let path = assigned[pi];
            if (!path) {
                path = [];
                survivingPaths.push(path);
            }
            const proj = projs[pi];
            const tail = path[path.length - 1];
            if (!tail || Math.abs(tail.x - proj.x) > 2 || Math.abs(tail.y - proj.y) > 2) {
                path.push({ x: proj.x, y: proj.y });
            }
            renderQueue.push({ proj, path });
        }
        projPaths.set(key, survivingPaths);
    }

    // Drop paths for keys with no projectiles in this frame
    for (const key of Array.from(projPaths.keys())) {
        if (!projsByKey.has(key)) projPaths.delete(key);
    }

    for (const { proj, path } of renderQueue) {
        const throwerSlot = proj.throwerSlot !== 0xFF ? proj.throwerSlot : -1;
        const throwerTeam = throwerSlot >= 0 ? roster[throwerSlot]?.team : null;
        const teamRgb = throwerTeam === 'T' ? '222,155,53' : throwerTeam === 'CT' ? '93,121,174' : '200,200,200';
        const pos = worldToCanvas(proj.x, proj.y, config, canvasSize);

        if (path.length >= 2) {
            ctx.beginPath();
            ctx.setLineDash([4, 4]);
            const start = worldToCanvas(path[0].x, path[0].y, config, canvasSize);
            ctx.moveTo(start.x, start.y);
            for (let i = 1; i < path.length; i++) {
                const pt = worldToCanvas(path[i].x, path[i].y, config, canvasSize);
                ctx.lineTo(pt.x, pt.y);
            }
            ctx.strokeStyle = `rgba(${teamRgb}, 0.7)`;
            ctx.lineWidth = 1.5;
            ctx.stroke();
            ctx.setLineDash([]);
        }

        const weaponId = PROJ_TO_WEAPON[proj.type];
        const projImg = weaponId != null ? preloadedIcons?.weapons[weaponId] : null;

        if (projImg) {
            const h = 12;
            const nw = projImg.naturalWidth || projImg.width || 10;
            const nh = projImg.naturalHeight || projImg.height || 10;
            const w = h * (nw / nh);
            ctx.fillStyle = `rgba(${teamRgb}, 0.6)`;
            ctx.beginPath();
            ctx.roundRect(pos.x - w / 2 - 2, pos.y - h / 2 - 2, w + 4, h + 4, 3);
            ctx.fill();
            ctx.drawImage(projImg, pos.x - w / 2, pos.y - h / 2, w, h);
        } else {
            ctx.beginPath();
            ctx.arc(pos.x, pos.y, 3.5, 0, Math.PI * 2);
            ctx.fillStyle = `rgba(${teamRgb}, 0.8)`;
            ctx.fill();
        }
    }

    // Players
    for (let i = 0; i < frame.players.length && i < roster.length; i++) {
        const player = frame.players[i];
        const entry = roster[i];
        const pos = worldToCanvas(player.x, player.y, config, canvasSize);
        const isT = entry.team === 'T';
        const baseColor = isT ? T_COLOR : CT_COLOR;
        const lightColor = isT ? T_COLOR_LIGHT : CT_COLOR_LIGHT;
        const name = getPlayerName(roster, i, playerNames);

        if (!player.isAlive) {
            // Skip dead player X if position is (0,0) — no valid pawn data
            if (player.x === 0 && player.y === 0) continue;
            // Dead player X
            ctx.save();
            ctx.globalAlpha = 0.25;
            ctx.strokeStyle = DEAD_COLOR;
            ctx.lineWidth = 2;
            ctx.beginPath();
            ctx.moveTo(pos.x - 5, pos.y - 5);
            ctx.lineTo(pos.x + 5, pos.y + 5);
            ctx.moveTo(pos.x + 5, pos.y - 5);
            ctx.lineTo(pos.x - 5, pos.y + 5);
            ctx.stroke();
            ctx.restore();
            continue;
        }

        const radius = (player.flags & (1 << 7)) ? PLAYER_RADIUS * 0.8 : PLAYER_RADIUS;

        // View cone (triangle shape like CS2Lens)
        // CS2 yaw: 0=east, 90=north. Radar: +X=right, +Y=down (Y inverted from world)
        // Convert: screen angle = -yaw (negate because screen Y is flipped)
        const yawRad = -player.yaw * (Math.PI / 180);
        const coneHalfRad = VIEW_CONE_ANGLE * (Math.PI / 180);
        ctx.beginPath();
        ctx.moveTo(pos.x, pos.y);
        ctx.lineTo(
            pos.x + Math.cos(yawRad - coneHalfRad) * VIEW_CONE_LENGTH,
            pos.y + Math.sin(yawRad - coneHalfRad) * VIEW_CONE_LENGTH,
        );
        ctx.lineTo(
            pos.x + Math.cos(yawRad + coneHalfRad) * VIEW_CONE_LENGTH,
            pos.y + Math.sin(yawRad + coneHalfRad) * VIEW_CONE_LENGTH,
        );
        ctx.closePath();
        ctx.fillStyle = `${baseColor}55`;
        ctx.fill();

        // Player circle - filled with team color, white border
        ctx.beginPath();
        ctx.arc(pos.x, pos.y, radius, 0, Math.PI * 2);
        ctx.fillStyle = baseColor;
        ctx.fill();
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 1.5;
        ctx.stroke();

        // Inner dot
        ctx.beginPath();
        ctx.arc(pos.x, pos.y, 2, 0, Math.PI * 2);
        ctx.fillStyle = '#ffffff';
        ctx.fill();

        // Flash overlay — computed from flash_blind events
        // Find the most recent flash_blind event for this player that's still active
        let flashRemainSecs = 0;
        for (let fi = flashBlinds.length - 1; fi >= 0; fi--) {
            const fb = flashBlinds[fi];
            if (fb.victimSlot !== i) continue;
            if (fb.frameIdx > currentFrameIdx) continue;
            const elapsedFrames = currentFrameIdx - fb.frameIdx;
            const elapsedSecs = elapsedFrames / 64; // tick rate
            const remaining = fb.durationSeconds - elapsedSecs;
            if (remaining > 0) flashRemainSecs = remaining;
            break; // most recent event for this player
        }
        if (flashRemainSecs > 0.1) {
            const flashAlpha = Math.min(flashRemainSecs / 3, 0.75);
            // White tint over the player circle
            ctx.beginPath();
            ctx.arc(pos.x, pos.y, radius, 0, Math.PI * 2);
            ctx.fillStyle = `rgba(255, 255, 255, ${flashAlpha})`;
            ctx.fill();
            // Outer glow
            ctx.beginPath();
            ctx.arc(pos.x, pos.y, radius + 4, 0, Math.PI * 2);
            ctx.fillStyle = `rgba(255, 255, 255, ${flashAlpha * 0.3})`;
            ctx.fill();
            // Duration badge (small circle with seconds)
            const secs = Math.ceil(flashRemainSecs);
            const badgeX = pos.x + radius + 5;
            const badgeY = pos.y - radius - 2;
            ctx.beginPath();
            ctx.arc(badgeX, badgeY, 7, 0, Math.PI * 2);
            ctx.fillStyle = 'rgba(255, 255, 255, 0.85)';
            ctx.fill();
            ctx.strokeStyle = 'rgba(0, 0, 0, 0.4)';
            ctx.lineWidth = 1;
            ctx.stroke();
            ctx.font = 'bold 9px Arial';
            ctx.fillStyle = '#000000';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(secs.toString(), badgeX, badgeY);
        }

        // Player name label (like CS2Lens - colored background pill)
        ctx.font = 'bold 9px Arial, sans-serif';
        const textWidth = ctx.measureText(name).width;
        const pillWidth = textWidth + 8;
        const pillHeight = 13;
        const pillX = pos.x - pillWidth / 2;
        const pillY = pos.y - radius - pillHeight - 3;

        // Pill background
        ctx.fillStyle = `${baseColor}cc`;
        ctx.beginPath();
        ctx.roundRect(pillX, pillY, pillWidth, pillHeight, 3);
        ctx.fill();

        // Name text
        ctx.fillStyle = '#ffffff';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(name, pos.x, pillY + pillHeight / 2);

        // Weapon icon above name pill
        const weaponIcon = preloadedIcons?.weapons[player.weaponId];
        if (weaponIcon) {
            const iconH = 10;
            const iconNw = weaponIcon.naturalWidth || weaponIcon.width || 100;
            const iconNh = weaponIcon.naturalHeight || weaponIcon.height || 50;
            const iconW = Math.min(iconH * (iconNw / iconNh), 40);
            const iconX = pos.x - iconW / 2;
            const iconY = pillY - iconH - 2;
            ctx.drawImage(weaponIcon, iconX, iconY, iconW, iconH);
        }
    }

    // Bomb indicator — use C4 icon if loaded; only pulse when planted
    if (frame.world.bombX !== 0 || frame.world.bombY !== 0) {
        const bombPos = worldToCanvas(frame.world.bombX, frame.world.bombY, config, canvasSize);

        if (bombDefused) {
            // Defused — show a blue completed ring and faded bomb icon
            const bombRingRadius = 12;
            ctx.beginPath();
            ctx.arc(bombPos.x, bombPos.y, bombRingRadius, 0, Math.PI * 2);
            ctx.strokeStyle = 'rgba(93, 121, 174, 0.8)';
            ctx.lineWidth = 2.5;
            ctx.stroke();

            // Faded bomb icon
            ctx.globalAlpha = 0.35;
            const bombIcon = preloadedIcons?.weapons[80];
            if (bombIcon) {
                const bh = 14;
                const bnw = bombIcon.naturalWidth || bombIcon.width || 10;
                const bnh = bombIcon.naturalHeight || bombIcon.height || 10;
                const bw = bh * (bnw / bnh);
                ctx.drawImage(bombIcon, bombPos.x - bw / 2, bombPos.y - bh / 2, bw, bh);
            }
            ctx.globalAlpha = 1;
        } else if (isBombPlanted) {
            const BOMB_TIMER = 40; // seconds
            const bombRingRadius = 12;

            // Pulsing red glow (only when planted)
            const pulsePhase = (currentFrameIdx % 60) / 60;
            const glowRadius = 8 + Math.sin(pulsePhase * Math.PI * 2) * 3;
            ctx.beginPath();
            ctx.arc(bombPos.x, bombPos.y, glowRadius, 0, Math.PI * 2);
            ctx.fillStyle = `rgba(255, 50, 50, ${0.15 + Math.sin(pulsePhase * Math.PI * 2) * 0.1})`;
            ctx.fill();

            // Count-up ring + defuse time markers
            if (bombPlantFrame != null) {
                const tickRate = 64;
                const elapsedSecs = (currentFrameIdx - bombPlantFrame) / tickRate;
                const elapsed = Math.min(elapsedSecs / BOMB_TIMER, 1);
                const DEFUSE_NO_KIT = 10; // seconds
                const DEFUSE_WITH_KIT = 5; // seconds
                const lineW = 2.5;

                if (elapsed < 1) {
                    // Background ring (full circle, dim)
                    ctx.beginPath();
                    ctx.arc(bombPos.x, bombPos.y, bombRingRadius, 0, Math.PI * 2);
                    ctx.strokeStyle = 'rgba(255, 50, 50, 0.2)';
                    ctx.lineWidth = lineW;
                    ctx.stroke();

                    // No-kit defuse deadline marker (orange) at 30s elapsed (10s remaining)
                    const noKitFrac = (BOMB_TIMER - DEFUSE_NO_KIT) / BOMB_TIMER;
                    const noKitStart = -Math.PI / 2 + Math.PI * 2 * noKitFrac;
                    ctx.beginPath();
                    ctx.arc(bombPos.x, bombPos.y, bombRingRadius, noKitStart - 0.04, noKitStart + 0.04);
                    ctx.strokeStyle = 'rgba(255, 160, 40, 0.9)';
                    ctx.lineWidth = lineW;
                    ctx.stroke();

                    // With-kit defuse deadline marker (blue) at 35s elapsed (5s remaining)
                    const kitFrac = (BOMB_TIMER - DEFUSE_WITH_KIT) / BOMB_TIMER;
                    const kitStart = -Math.PI / 2 + Math.PI * 2 * kitFrac;
                    ctx.beginPath();
                    ctx.arc(bombPos.x, bombPos.y, bombRingRadius, kitStart - 0.04, kitStart + 0.04);
                    ctx.strokeStyle = 'rgba(93, 121, 174, 0.9)';
                    ctx.lineWidth = lineW;
                    ctx.stroke();

                    // Elapsed arc (counts up from top, clockwise)
                    ctx.beginPath();
                    ctx.arc(bombPos.x, bombPos.y, bombRingRadius, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * elapsed);
                    ctx.strokeStyle = `rgba(255, 50, 50, ${0.5 + elapsed * 0.5})`;
                    ctx.lineWidth = lineW;
                    ctx.stroke();

                    // Defuse progress — blue arc from 12 o'clock, filling at defuse speed
                    // Blue races the red: if blue reaches full circle first, defuse succeeds
                    if (activeDefuse) {
                        const defuseTime = activeDefuse.hasKit ? DEFUSE_WITH_KIT : DEFUSE_NO_KIT;
                        const defuseElapsedSecs = (currentFrameIdx - activeDefuse.startFrame) / tickRate;
                        const defuseFrac = Math.min(defuseElapsedSecs / defuseTime, 1);

                        const blueEnd = -Math.PI / 2 + Math.PI * 2 * defuseFrac;

                        ctx.beginPath();
                        ctx.arc(bombPos.x, bombPos.y, bombRingRadius, -Math.PI / 2, blueEnd);
                        ctx.strokeStyle = 'rgba(93, 121, 174, 1)';
                        ctx.lineWidth = lineW + 2;
                        ctx.stroke();
                    }
                }
            }
        }

        const bombIcon = preloadedIcons?.weapons[80]; // C4 = weapon ID 80
        if (bombIcon) {
            const bh = 14;
            const bnw = bombIcon.naturalWidth || bombIcon.width || 10;
            const bnh = bombIcon.naturalHeight || bombIcon.height || 10;
            const bw = bh * (bnw / bnh);
            ctx.drawImage(bombIcon, bombPos.x - bw / 2, bombPos.y - bh / 2, bw, bh);
        } else {
            // Fallback red dot
            ctx.beginPath();
            ctx.arc(bombPos.x, bombPos.y, 5, 0, Math.PI * 2);
            ctx.fillStyle = '#ff3333';
            ctx.fill();
            ctx.strokeStyle = '#ffffff';
            ctx.lineWidth = 1.5;
            ctx.stroke();
            ctx.font = 'bold 7px Arial';
            ctx.fillStyle = '#ffffff';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText('C4', bombPos.x, bombPos.y + 0.5);
        }
    }

    // Bomb explosion effect — expanding shockwave + central flash for ~1s after detonation.
    // Generic visual; we don't know the true per-map damage radius from the demo.
    if (bombExploded && currentFrameIdx >= bombExploded.frameIdx) {
        const BOOM_FRAMES = 64; // ~1.0s at 64 tick
        const age = currentFrameIdx - bombExploded.frameIdx;
        if (age < BOOM_FRAMES) {
            const t = age / BOOM_FRAMES; // 0 → 1
            const ePos = worldToCanvas(bombExploded.x, bombExploded.y, config, canvasSize);
            const peakR = worldRadius(450, config, canvasSize);

            // Central flash (bright at t=0, fades fast)
            const flashAlpha = Math.max(0, 1 - t * 2);
            if (flashAlpha > 0) {
                const flashR = worldRadius(120, config, canvasSize);
                const grd = ctx.createRadialGradient(ePos.x, ePos.y, 0, ePos.x, ePos.y, flashR);
                grd.addColorStop(0, `rgba(255, 240, 200, ${flashAlpha})`);
                grd.addColorStop(0.5, `rgba(255, 140, 40, ${flashAlpha * 0.55})`);
                grd.addColorStop(1, 'rgba(255, 60, 20, 0)');
                ctx.fillStyle = grd;
                ctx.beginPath();
                ctx.arc(ePos.x, ePos.y, flashR, 0, Math.PI * 2);
                ctx.fill();
            }

            // Expanding shockwave ring
            const ringR = peakR * t;
            const ringAlpha = Math.max(0, 1 - t);
            ctx.beginPath();
            ctx.arc(ePos.x, ePos.y, ringR, 0, Math.PI * 2);
            ctx.strokeStyle = `rgba(255, 120, 30, ${ringAlpha * 0.9})`;
            ctx.lineWidth = 3;
            ctx.stroke();

            // Soft trailing ring just inside, for thickness
            if (ringR > 4) {
                ctx.beginPath();
                ctx.arc(ePos.x, ePos.y, ringR - 4, 0, Math.PI * 2);
                ctx.strokeStyle = `rgba(255, 220, 120, ${ringAlpha * 0.5})`;
                ctx.lineWidth = 1.5;
                ctx.stroke();
            }
        }
    }

    // Muzzle flash — visible glow around shooter
    const recentShots = tracers.filter(t => currentFrameIdx - t.frameIdx < 10 && currentFrameIdx >= t.frameIdx);
    for (const shot of recentShots) {
        const pos = worldToCanvas(shot.shooterX, shot.shooterY, config, canvasSize);
        const age = (currentFrameIdx - shot.frameIdx) / 10;
        const alpha = Math.max(0, 1 - age);
        const team = roster[shot.shooterSlot]?.team;
        const flashRgb = team === 'T' ? '255,180,50' : '150,200,255';

        // Outer glow ring
        const outerR = PLAYER_RADIUS + 6 + age * 4;
        ctx.beginPath();
        ctx.arc(pos.x, pos.y, outerR, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(${flashRgb}, ${alpha * 0.7})`;
        ctx.lineWidth = 2;
        ctx.stroke();

        // Inner flash
        ctx.beginPath();
        ctx.arc(pos.x, pos.y, PLAYER_RADIUS + 2, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${flashRgb}, ${alpha * 0.4})`;
        ctx.fill();
    }

    // Hit tracers — line from attacker to victim only on damage events
    const recentHits = hitMarkers.filter(h => currentFrameIdx - h.frameIdx < 20 && currentFrameIdx >= h.frameIdx);
    for (const hit of recentHits) {
        const from = worldToCanvas(hit.attackerX, hit.attackerY, config, canvasSize);
        const to = worldToCanvas(hit.victimX, hit.victimY, config, canvasSize);
        const age = (currentFrameIdx - hit.frameIdx) / 20;
        const alpha = Math.max(0, 0.6 - age * 0.6);
        const team = roster[hit.attackerSlot]?.team;
        const color = team === 'T' ? '255,200,100' : '150,200,255';

        ctx.beginPath();
        ctx.moveTo(from.x, from.y);
        ctx.lineTo(to.x, to.y);
        ctx.strokeStyle = `rgba(${color}, ${alpha})`;
        ctx.lineWidth = 1.5;
        ctx.stroke();

        // Impact — red glow around victim
        const impactAlpha = Math.max(0, 1 - age);

        // Red ring around victim
        const impactR = PLAYER_RADIUS + 5 + age * 4;
        ctx.beginPath();
        ctx.arc(to.x, to.y, impactR, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(255, 60, 30, ${impactAlpha * 0.8})`;
        ctx.lineWidth = 2.5;
        ctx.stroke();

        // Red fill glow
        ctx.beginPath();
        ctx.arc(to.x, to.y, PLAYER_RADIUS + 3, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255, 50, 30, ${impactAlpha * 0.35})`;
        ctx.fill();
    }

    // Kill feed moved to HTML overlay (ReplayKillFeed.tsx)

    // Reset text alignment
    ctx.textAlign = 'left';
    ctx.textBaseline = 'alphabetic';
}

// ─── Multi-level (Nuke) Z-filtering helpers ────────────────────────────────

type Level = 'upper' | 'lower';

const HIDDEN_PLAYER: PlayerFrame = {
    x: 0, y: 0, z: 0, yaw: 0, pitch: 0, hp: 0, armor: 0,
    weaponId: 0, flags: 0, flashRemain: 0, money: 0,
    ammoClip: 0, ammoReserve: 0, equipValue: 0,
    utilityFlash: 0, utilitySmoke: 0, utilityHE: 0, utilityFire: 0,
    zoomLevel: 0, ping: 0, velX: 0, velY: 0, isAlive: false,
};

/** Check if a coordinate should appear on the given level.
 *  "Both" only if Z is between thresholds AND (x,y) is within the bothZone (ramp/stairs). */
export function isOnLevel(
    z: number, level: Level, zUpper: number, zLower: number,
    x?: number, y?: number, bothZone?: MultiLevelMapConfig['bothZone'],
): boolean {
    if (z >= zUpper) return level === 'upper';
    if (z <= zLower) return level === 'lower';
    // Between thresholds: only "both" if inside the ramp/stairs XY zone
    if (bothZone && x !== undefined && y !== undefined) {
        const inZone = x >= bothZone.minX && x <= bothZone.maxX && y >= bothZone.minY && y <= bothZone.maxY;
        if (inZone) return true; // show on both
    }
    // Outside bothZone (or no zone defined): use hard midpoint
    const midZ = (zUpper + zLower) / 2;
    return level === 'upper' ? z >= midZ : z < midZ;
}

/** Filter a frame's players to only those on the given level. */
export function filterFrameByLevel(
    frame: Frame, level: Level, zUpper: number, zLower: number,
    bothZone?: MultiLevelMapConfig['bothZone'],
): Frame {
    const midZ = (zUpper + zLower) / 2;
    return {
        tick: frame.tick,
        players: frame.players.map(p => {
            if (!p.isAlive) {
                if (p.x === 0 && p.y === 0) return HIDDEN_PLAYER;
                return (level === 'upper' ? p.z >= midZ : p.z < midZ) ? p : HIDDEN_PLAYER;
            }
            return isOnLevel(p.z, level, zUpper, zLower, p.x, p.y, bothZone) ? p : HIDDEN_PLAYER;
        }),
        world: frame.world,
    };
}

/** Filter world entities by level using entity Z data. */
export function filterWorldByLevel(
    world: WorldState,
    entityZData: EntityZData,
    currentFrame: Frame,
    currentFrameIdx: number,
    level: Level,
    zUpper: number,
    zLower: number,
    bothZone?: MultiLevelMapConfig['bothZone'],
): WorldState {
    const midZ = (zUpper + zLower) / 2;
    // Stationary entities use hard midpoint — they're on one level only
    const entityInLevel = (id: number) => {
        const z = entityZData.entityZ.get(id);
        if (z === undefined) return false;
        return level === 'upper' ? z >= midZ : z < midZ;
    };
    // Projectiles: use thrower's current Z (most reliable — they're on the same level as their nade).
    // Fall back to throw-event Z only if thrower is dead.
    const projInLevel = (proj: { throwerSlot: number; x: number; y: number }) => {
        if (proj.throwerSlot === 0xff) return level === 'upper';
        // Primary: thrower's current Z (they're alive and on the same level)
        if (proj.throwerSlot < currentFrame.players.length) {
            const p = currentFrame.players[proj.throwerSlot];
            if (p && p.isAlive) return level === 'upper' ? p.z >= midZ : p.z < midZ;
        }
        // Fallback (thrower dead): use most recent throw event Z
        const throws = entityZData.slotThrows.get(proj.throwerSlot);
        if (throws) {
            let bestZ: number | undefined;
            for (const t of throws) {
                if (t.frameIdx <= currentFrameIdx) bestZ = t.z;
            }
            if (bestZ !== undefined) return level === 'upper' ? bestZ >= midZ : bestZ < midZ;
        }
        return level === 'upper';
    };

    // Bomb: determine level from carrier Z, planter Z, or nearest player
    let bombOnLevel = false;
    if (world.bombX !== 0 || world.bombY !== 0) {
        let bombZ: number | undefined;
        for (const p of currentFrame.players) {
            if (p.isAlive && (p.flags & (1 << 5))) { bombZ = p.z; break; }
        }
        if (bombZ === undefined) bombZ = entityZData.entityZ.get(-1);
        if (bombZ === undefined) {
            let minDist = Infinity;
            for (const p of currentFrame.players) {
                if (!p.isAlive || (p.x === 0 && p.y === 0)) continue;
                const dx = p.x - world.bombX, dy = p.y - world.bombY;
                const dist = dx * dx + dy * dy;
                if (dist < minDist) { minDist = dist; bombZ = p.z; }
            }
        }
        bombOnLevel = bombZ !== undefined ? isOnLevel(bombZ, level, zUpper, zLower) : level === 'upper';
    }

    // Filter projectiles: must be on this level AND not already landed as smoke/fire.
    // Check against the FULL (unfiltered) smoke/fire lists so that upper-level smokes
    // still suppress their projectile on the lower panel.
    const filteredProjectiles = world.projectiles.filter(p => {
        if (!projInLevel(p)) return false;
        // Skip smoke projectiles near any active smoke (regardless of level)
        if (p.type === 1 && world.smokes.some(s =>
            Math.abs(s.x - p.x) < 50 && Math.abs(s.y - p.y) < 50)) return false;
        // Skip molotov projectiles near any active fire
        if (p.type === 4 && world.fires.some(f =>
            Math.abs(f.x - p.x) < 50 && Math.abs(f.y - p.y) < 50)) return false;
        return true;
    });

    // Dropped weapons/grenades carry Z from v4+; older replays (z undefined)
    // fall back to showing on both panels.
    const filteredItems = world.droppedItems.filter(item => {
        if (item.z === undefined) return true;
        return isOnLevel(item.z, level, zUpper, zLower, item.x, item.y, bothZone);
    });

    return {
        bombX: bombOnLevel ? world.bombX : 0,
        bombY: bombOnLevel ? world.bombY : 0,
        projectiles: filteredProjectiles,
        smokes: world.smokes.filter(s => entityInLevel(s.entityId)),
        fires: world.fires.filter(f => entityInLevel(f.uniqueId)),
        droppedKits: world.droppedKits,
        droppedItems: filteredItems,
    };
}

/**
 * Build entity ID -> Z lookup from entity start events. Key -1 = bomb plant Z.
 * Also returns slotThrowZ: per-slot array of {frameIdx, z} for throw-time Z lookups.
 */
export interface EntityZData {
    entityZ: Map<number, number>;
    /** Per-slot throw events sorted by frameIdx, for matching in-flight projectiles */
    slotThrows: Map<number, Array<{ frameIdx: number; z: number }>>;
}

export function buildEntityZLookup(
    events: ReplayEvent[],
    frames: Frame[],
): EntityZData {
    const entityZ = new Map<number, number>();
    const slotThrows = new Map<number, Array<{ frameIdx: number; z: number }>>();

    for (const evt of events) {
        if (evt.type === 'smoke_throw') {
            const e = evt as SmokeThrowEvent;
            if (e.throwerSlot !== 0xff) {
                if (!slotThrows.has(e.throwerSlot)) slotThrows.set(e.throwerSlot, []);
                slotThrows.get(e.throwerSlot)!.push({ frameIdx: e.frameIdx, z: e.posZ });
            }
        } else if (evt.type === 'smoke_start' || evt.type === 'inferno_start' || evt.type === 'decoy_start') {
            const e = evt as EntityStartEvent;
            const frame = frames[Math.min(e.frameIdx, frames.length - 1)];
            if (frame && e.throwerSlot !== 0xff && e.throwerSlot < frame.players.length) {
                const z = frame.players[e.throwerSlot].z;
                entityZ.set(e.entityId, z);
                // Also record as a throw event for projectile matching
                if (!slotThrows.has(e.throwerSlot)) slotThrows.set(e.throwerSlot, []);
                slotThrows.get(e.throwerSlot)!.push({ frameIdx: e.frameIdx, z });
            }
        } else if (evt.type === 'bomb_plant') {
            const e = evt as BombPlantEvent;
            const frame = frames[Math.min(e.frameIdx, frames.length - 1)];
            if (frame && e.planterSlot < frame.players.length) {
                entityZ.set(-1, frame.players[e.planterSlot].z);
            }
        }
    }
    return { entityZ, slotThrows };
}

/** Filter event-derived arrays by the relevant player's Z at event time */
export function filterByPlayerZ<T extends { frameIdx: number }>(
    items: T[],
    frames: Frame[],
    getSlot: (item: T) => number,
    level: Level,
    zUpper: number,
    zLower: number,
): T[] {
    return items.filter(item => {
        const slot = getSlot(item);
        if (slot === 0xff) return false;
        const frame = frames[Math.min(item.frameIdx, frames.length - 1)];
        if (!frame || slot >= frame.players.length) return false;
        const p = frame.players[slot];
        return isOnLevel(p.z, level, zUpper, zLower);
    });
}
